<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faClone, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    // ##############################################################################################################
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { FormTemplatesRepository } from '../../../modules/toolsApi/forms/form-templates/form-templates.repository'
    import { FormTemplatesService } from '../../../modules/toolsApi/forms/form-templates/form-templates.service'
    import { FormTemplate } from '../../../modules/toolsApi/forms/form-templates/form-template'
    import type { CreateFormTemplateDto } from '../../../modules/toolsApi/forms/form-templates/dto/create-form-template.dto'
    import type { UpdateFormTemplateDto } from '../../../modules/toolsApi/forms/form-templates/dto/update-form-template.dto'
    import CustomerServiceTemplate from '../../customer-service/elements/customer-service-template.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine, GridToggle } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import { FormTemplateTypeEnum, FormTemplateTypeFriendlyEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'

    let show = false
    let step = 1
    let entities: FormTemplate[] = []
    let entity: FormTemplate = new FormTemplate()
    let totalModels = 999
    let changed = false

    async function copyEntity(id: string) {
        if (!confirm('Deseja mesmo duplicar este modelo?')) return
        await window.api.tools.forms.formTemplates.copy(id)
        await loadEntities()
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new FormTemplate()
            entity = await window.api.tools.forms.formTemplates.getOne(id)
            if (!entity.appearence) entity.appearence = 'default'
            if (!entity.requiredFields) entity.requiredFields = []
            console.log('form-template', entity)
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new FormTemplate()
        entity.form = []
        entity.paged = 0
        entity.showHeaders = true
        // Sem isto o campo nasce undefined, e o Zinput ligado por bind:value escreve de volta o
        // default da própria prop (null) — que o banco recusa, porque a coluna é uma lista não-nula.
        // O editEntity já fazia o mesmo ajuste; faltava no caminho de criação.
        entity.requiredFields = []
        step = 2
    }

    async function createEntity() {
        try {
            show = true
            const dto: CreateFormTemplateDto = {
                title: entity.title,
                form: entity.form.filter((el) => el !== undefined && el !== null),
                paged: entity.paged,
                appearence: entity.appearence,
                showHeaders: entity.showHeaders,
                type: entity.type,
                requiredFields: entity.requiredFields,
            }
            if (!validateDto(dto)) return
            entity = await window.api.tools.forms.formTemplates.create(dto)
            changed = false
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            const dto: UpdateFormTemplateDto = {
                title: entity.title,
                form: entity.form.filter((el) => el !== undefined && el !== null),
                paged: entity.paged,
                appearence: entity.appearence,
                showHeaders: entity.showHeaders,
                type: entity.type,
                requiredFields: entity.requiredFields,
            }
            if (!validateDto(dto)) return
            await window.api.tools.forms.formTemplates.update(entity.formTemplateId, dto)
            changed = false
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto: CreateFormTemplateDto | UpdateFormTemplateDto) {
        if (!dto.title) {
            window.error('Informe o título do modelo')
            return false
        }
        if (!dto.type) {
            window.error('Informe o tipo do modelo')
            return false
        }
        if (!dto.appearence) {
            window.error('Informe a aparência do modelo')
            return false
        }
        return true
    }

    async function saveEntity() {
        if (entity.formTemplateId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.tools.forms.formTemplates.getAll()
            totalModels = entities.filter((el) => el.system === true).length
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function toggleStatus(formTemplateId: string) {
        const current = entities.find((el) => el.formTemplateId === formTemplateId)
        await window.api.tools.forms.formTemplates.update(formTemplateId, { status: current.status === 100 ? 70 : 100 })
        await loadEntities()
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Modelos de Atendimento'
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Ativo', type: GridColTypeEnum.toggle }))
        grid.cols.push(new GridCol({ content: 'Nome do modelo' }))
        grid.cols.push(new GridCol({ content: 'Data de criação', size: 2, type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Data de edição', size: 2, type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        let badgeModel: GridBadge = {
            class: GridBadgeClassEnum.badge_default,
            color: null,
            label: 'modelo',
        }
        for (const item of entities) {
            const toggle = new GridToggle({
                callback: () => {
                    toggleStatus(item.formTemplateId)
                },
                checked: item.status === 100,
            })
            const btnEdit = new GridButton({
                callback: () => editEntity(item.formTemplateId),
                icon: faEdit,
                label: 'Editar',
            })
            const btns = []
            if (!item.system) btns.push(btnEdit)
            if (window.store.token.permissions.tools.customerService.write) {
                const btnCopy = new GridButton({
                    callback: () => copyEntity(item.formTemplateId),
                    icon: faClone,
                    label: 'Duplicar',
                })
                btns.push(btnCopy)
            }
            let line: GridLine = new GridLine()
            line.slots.push({ content: toggle })
            line.slots.push({ content: item.title, badge: item.system ? badgeModel : null })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.updatedAt })
            line.slots.push({ content: btns })
            grid.lines.push(line)
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.tools.forms.formTemplates.remove(entity.formTemplateId)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        if (changed) {
            if (!confirm('Deseja mesmo sair sem salvar?')) return
        }
        changed = false
        step = 1
    }

    onMount(async () => {
        await loadEntities()
    })

    async function addModels() {
        const res = await window.api.tools.forms.formTemplates.addModels()
        await loadEntities()
        console.log('res', res)
    }

    function templateOnChange() {
        changed = true
    }

    let formTypes = Object.keys(FormTemplateTypeEnum)
    let formTypesOptions = []
    for (const item of formTypes) {
        formTypesOptions.push({ value: item, label: FormTemplateTypeFriendlyEnum[item] })
    }

    const appearenceOptions = [
        { value: 'default', label: 'Padrão' },
        { value: 'continuous', label: 'Contínuo' },
        { value: 'titled_green', label: 'Destaque verde' },
        { value: 'titled_pink', label: 'Destaque rosa' },
    ]

    let requiredOptions: { value: string; label: string }[] = []
    requiredOptions = [
        { value: 'email', label: 'e-mail' },
        { value: 'address', label: 'endereço' },
        { value: 'document', label: 'documento' },
    ]
</script>

<LoadSpinner {show}></LoadSpinner>
{#if step === 1}
    <Content footer={true}>
        <div class="grid-act">
            <div class="spacer" />
            {#if window.store.token.permissions.tools.customerService.write}
                <button class="btn btn-primary" on:click={newEntity}><Fa icon={faPlusCircle} /> Novo modelo de atendimento</button>
            {/if}
        </div>
        <Zgrid class="mt-3" {grid} />
        {#if totalModels < 2 && window.store.token.permissions.tools.customerService.write}
            <div class="alert alert-primary mt-3">
                <div>Adicione os modelos padrão e tenha exemplos de documentos predefinidos.</div>
                <div>
                    <button on:click={addModels} class="btn btn-secondary">Adicionar modelos</button>
                </div>
            </div>
        {/if}
    </Content>
{/if}
{#if step === 2}
    <Content footer={true}>
        <div class="grid-act">
            <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer" />
            {#if window.store.token.permissions.tools.customerService.write}
                {#if !!entity.formTemplateId}
                    <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
                {/if}
                <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
            {/if}
        </div>
        <Zframe title="Modelo" class="mt-3">
            <ZContainer>
                <Zinput size={4} label="Nome do modelo" bind:value={entity.title}></Zinput>
                <Zinput size={4} label="Tipo de modelo" type="select" options={formTypesOptions} bind:value={entity.type}></Zinput>
                <Zinput size={4} label="Aparência" type="select" options={appearenceOptions} bind:value={entity.appearence}></Zinput>
                <Zinput size={4} label="Campos obrigatórios da pessoa" type="multiselect" options={requiredOptions} bind:value={entity.requiredFields}></Zinput>
                <Zinput size={8} label="Exibir cabeçalho e rodapé" type="toggle" bind:value={entity.showHeaders}></Zinput>
                <ZContainerCol>
                    <CustomerServiceTemplate
                        on:change={templateOnChange}
                        showHeaders={entity.showHeaders}
                        appearence={entity.appearence}
                        editable={true}
                        bind:items={entity.form}
                    />
                </ZContainerCol>
            </ZContainer>
        </Zframe>
    </Content>
{/if}

<style>
    .alert {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .alert > div:first-child {
        flex-grow: 1;
        margin-bottom: 8px;
    }
</style>
